import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';
import { vocabularyApi } from '../../api/global';
import {
  FOLDING,
  PAPER_TABS,
  PAPER_SET,
  ORDER_TYPE,
  PAPER_CONFIG,
  AUDIO_PAPER_TYPE,
} from '../../data/common';
import { strParse } from '../../lib/util';
import HeaderBtnBox from '../../components/Vocabulary/HeaderBtnBox';
import ManualImage from '../../components/Vocabulary/ManualImage';
import UnitChoice from '../../components/Vocabulary/UnitChoice';
import QuestionTypeChoice from '../../components/Vocabulary/QuestionTypeChoice';
import VocabularyChoice from '../../components/Vocabulary/VocabularyChoice';
import VocaTestPaper from '../../components/Vocabulary/VocaTestPaper';
import DownloadModal from '../../components/Vocabulary/DownloadModal';
import PrintSet from '../../components/Vocabulary/PrintSet';
import RecordSetModal from '../../components/Vocabulary/RecordSetModal';
import AlertModal from '../../components/common/AlertModal';

const TestPaperInit = {
  orgLogoFile: null,
  orgLogoUrl: null,
  logoAppearYN: false,
  orgName: '',
  className: '',
  teacherName: '',
  paperName: '',
  paperDiv: 2,
  fontSize: PAPER_CONFIG.FONT_SIZE.NORMAL,
  spacing: PAPER_CONFIG.LINE_SPACING.NORMAL,
  viewMode: PAPER_TABS.TEST_PAPER,
  vocaFirstWordShow: false,
  onlyFirstMeaning: [],
  examKorShow: false,
};

function Vocabulary() {
  const isSaved = useRef(true);
  const location = useLocation();
  const [isFolding, setIsFolding] = useState(FOLDING);
  const [paperTab, setPaperTab] = useState(PAPER_TABS.TEST_PAPER);
  const [printSetOpen, setPrintSetOpen] = useState(PAPER_SET);
  const [failedVocaAlert, setFailedVocaAlert] = useState([]);
  const [dowloadModalOpen, setDownloadModalOpen] = useState(false);
  const [hideTestPaper, setHideTestPaper] = useState(false);
  const [myManuals, setMyManuals] = useState([]);
  const [manualInfo, setManulInfo] = useState(null);
  const [unitList, setUnitList] = useState([]);
  const [checkedUnitIdxList, setCheckedUnitIdxList] = useState([]);
  const [orderingCheck, setOrderingCheck] = useState(ORDER_TYPE.BOOK); // 교재/알파벳/랜덤순
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [printSetTrigger, setPrintSetTrigger] = useState(false); // 시험지설정, 텍스트변경시 pdf 재생성
  const [testPaperConfig, setTestPaperConfig] = useState(TestPaperInit);
  const [audioType, setAudioType] = useState(AUDIO_PAPER_TYPE.ENG);
  const [audioGapAmount, setAudioGapAmount] = useState(5);
  const [recordSetModalOpen, setRecordSetModalOpen] = useState(false);
  const [checkedStatistics, setCheckedStatistics] = useState([1]); // 표제어만 디폴트
  const [vocabularyList, setVocabularyList] = useState([]);
  const [generatedPaper, setGeneratedPaper] = useState([]);

  // 매뉴얼 정보 세팅
  useEffect(() => {
    if (location.state === null) return;
    setManulInfo(location.state.manual);
    setMyManuals(location.state.myManuals);
  }, [location, manualInfo, myManuals]);

  // 교재 이동시 리셋
  useEffect(() => {
    if (location.state !== null && location.state.isExchange) {
      setCheckedUnitIdxList([]);
      setVocabularyList([]);
      setTestPaperConfig(TestPaperInit);
      setGeneratedPaper([]);
    }
  }, [location.state]);

  // 매뉴얼에 속한 유닛 호출
  useEffect(() => {
    (async () => {
      if (manualInfo === null) return;
      try {
        const { data } = await vocabularyApi.getUnits(manualInfo.idx);
        setUnitList(data.units);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [manualInfo]);

  // 어휘 세팅 변경시 시험지화면 업데이트 유도
  useEffect(() => {
    if (vocabularyList?.length === 0) setHideTestPaper(false);
    if (!hideTestPaper) {
      setHideTestPaper(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vocabularyList]);

  // 시험지 생성하고 리턴 시험지 저장
  const saveFinalVocaList = useCallback(async () => {
    try {
      const apiParams = {
        bookIdx: manualInfo.idx,
        unitIds: checkedUnitIdxList,
        vocas: vocabularyList
          .filter(v => v.shouldShow && v.checked && v.chosenType !== null)
          .map(v => {
            const result = {
              vocaIdx: v.idx,
              problemTypes: [v.chosenType],
            };
            if (v.chosenType === 'F') {
              result.audioQuestionType = audioType;
            }
            if (testPaperConfig.onlyFirstMeaning?.includes(v.chosenType)) {
              result.firstMeaningOnly = true;
            }
            return result;
          }),
      };
      const { status, data } = await vocabularyApi.createTestPaper(apiParams);
      console.warn('generatedPaper response:', status);
      if (status === 200) {
        // 알파벳 순 정렬
        const sortedData = data.questions.sort((a, b) =>
          a.type.localeCompare(b.type),
        );
        // 시험지 타입별 시작번호 추가
        let addedStartNo = [];
        sortedData.forEach((type, idx) => {
          if (idx === 0) {
            addedStartNo.push({ ...type, startNo: 1 });
            return;
          }
          addedStartNo.push({
            ...type,
            startNo:
              addedStartNo[idx - 1]?.questions?.length +
              addedStartNo[idx - 1].startNo,
          });
        });
        setGeneratedPaper(addedStartNo);
        isSaved.current = false;
      }
      if (data?.failedVocas?.length >= 1) {
        setFailedVocaAlert(data?.failedVocas);
      }
    } catch (error) {
      console.error(error);
    }
  }, [
    manualInfo,
    checkedUnitIdxList,
    testPaperConfig,
    vocabularyList,
    audioType,
  ]);

  const openCount = Object.values(isFolding)?.filter(v => v === false)?.length;

  // 폴딩 접히 때의 프린트셋 열기 설정
  useEffect(() => {
    if (printSetOpen.set || printSetOpen.print) {
      if (openCount >= 2) {
        setIsFolding({
          unit: true,
          type: true,
          voca: false,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printSetOpen]);

  useEffect(() => {
    if (openCount >= 2) {
      setPrintSetOpen({ set: false, print: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFolding]);

  // pdf 시험지 인쇄하기
  const printPdf = () => {
    if (pdfBlob === null) return;
    const url = window.URL.createObjectURL(pdfBlob);
    const iframe = document.createElement('iframe'); // pdf 화면을 iframe에 숨김
    iframe.style.display = 'none';
    iframe.onload = () => {
      if (!iframe.contentWindow) return;
      iframe.contentWindow.onafterprint = () => {
        window.URL.revokeObjectURL(url);
        iframe.remove();
      };
      iframe.contentWindow.print();
    };
    iframe.src = url;
    document.body.appendChild(iframe);
  };

  // pdf 시험지 다운로드
  const downloadPdf = () => {
    if (pdfBlob === null) return;
    const paperName = testPaperConfig?.paperName || '';
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(pdfBlob);
    const suffix =
      paperTab === PAPER_TABS.TEST_PAPER
        ? '_시험지'
        : paperTab === PAPER_TABS.VOCABULARY_LIST
          ? '_어휘리스트'
          : paperTab === PAPER_TABS.ANSWER_TEST_PAPER
            ? '_정답표시'
            : '_정답';
    link.download = `${paperName}${suffix}.pdf`;
    link.click();
  };

  // '단어목록' 탭에서 공통 단어목록 생성
  const commonPaperVocaList = useMemo(() => {
    const result = [];
    if (generatedPaper?.length >= 1) {
      generatedPaper?.forEach(vocaTypeSet => {
        if (vocaTypeSet.type === 'A') {
          vocaTypeSet.questions.forEach(question => {
            result.push({
              type: 'A',
              kor: question.answer,
              eng: question.question,
            });
          });
        } else if (vocaTypeSet.type === 'B') {
          vocaTypeSet.questions.forEach(question => {
            result.push({
              type: 'B',
              kor: question.question,
              eng: question.answer,
            });
          });
        } else if (vocaTypeSet.type === 'C') {
          vocaTypeSet.questions.forEach(question => {
            result.push({
              type: 'C',
              kor: question?.meaning ?? '',
              eng: question.word,
              word: question.word,
            });
          });
        } else if (vocaTypeSet.type === 'D') {
          vocaTypeSet.questions.forEach(question => {
            result.push({
              type: 'D',
              kor: question?.meaning ?? '',
              eng: question.word,
            });
          });
        } else if (vocaTypeSet.type === 'E') {
          vocaTypeSet.questions.forEach(question => {
            result.push({
              type: 'E',
              kor: question?.meaning ?? '',
              eng: question.word,
            });
          });
        } else if (vocaTypeSet.type === 'F') {
          vocaTypeSet.questions.forEach(question => {
            result.push({
              type: 'F',
              kor: strParse(question.meanings),
              eng: question.answer,
              word: question.answer,
            });
          });
        } else if (vocaTypeSet.type === 'G') {
          vocaTypeSet.questions.forEach(question => {
            result.push({
              type: 'G',
              kor: question?.meaning ?? '',
              eng: question.answer,
            });
          });
        } else if (vocaTypeSet.type === 'H') {
          vocaTypeSet.questions.forEach(question => {
            if (question?.options?.length !== 4) return;
            result.push({
              type: 'H',
              eng: question?.question,
              kor: question?.options[question.answer - 1],
            });
          });
        } else if (vocaTypeSet.type === 'I') {
          vocaTypeSet.questions.forEach(question => {
            if (question?.options?.length !== 4) return;
            result.push({
              type: 'I',
              eng: question?.options[question.answer - 1],
              kor: question?.question,
            });
          });
        } else if (vocaTypeSet.type === 'J') {
          vocaTypeSet.questions.forEach(question => {
            if (question?.options?.length !== 4) return;
            result.push({
              type: 'I',
              eng: question.word,
              kor: question?.options[question.answer - 1],
            });
          });
        } else if (vocaTypeSet.type === 'K') {
          vocaTypeSet.questions.forEach(question => {
            if (question?.options?.length !== 4) return;
            result.push({
              type: 'K',
              eng: question.word,
              kor: question?.meaning ?? '',
            });
          });
        } else if (vocaTypeSet.type === 'L') {
          vocaTypeSet.questions.forEach(question => {
            if (question?.options?.length !== 4) return;
            result.push({
              type: 'L',
              eng: question.question,
              kor: question?.meaning ?? '',
            });
          });
        } else if (vocaTypeSet.type === 'M') {
          vocaTypeSet.questions.forEach(question => {
            if (question?.options?.length !== 4) return;
            result.push({
              type: 'M',
              eng: question?.options[question.answer - 1],
              kor: question?.meaning ?? '',
            });
          });
        }
      });
    }
    return result;
  }, [generatedPaper]);

  return (
    <main className="pageContainer">
      <div className="flex justify-start h-full w-full">
        <section className="w-[60px] h-full min-w-[60px] bg-[#F1F5F9] border-r border-[#BEBEBE] flex flex-col justify-start items-center gap-[8px] py-[10px] px-[8px]">
          <ManualImage
            src={manualInfo?.book_imagelink}
            alt={manualInfo?.book_title}
          />
          {myManuals?.length >= 1 &&
            myManuals
              .filter(m => m.book.idx !== manualInfo.idx)
              .map((manual, index) => {
                return (
                  <ManualImage
                    key={`${manual.idx}-${String(index)}`}
                    src={manual?.book?.book_imagelink}
                    alt={manual?.book_title}
                    manual={manual?.book}
                    myManuals={myManuals}
                    setIsFolding={setIsFolding}
                    setGeneratedPaper={setGeneratedPaper}
                  />
                );
              })}
        </section>
        <section className="border grow h-full flex flex-col justify-start items-start">
          <div className="bg-[#F1F5F9] border-b border-[#BEBEBE] w-full flex justify-between items-center px-[18px] min-h-[58px] h-[58px]">
            <div className="font-[700] text-[20px]">
              {manualInfo?.book_title}
            </div>
            <HeaderBtnBox
              isSaved={isSaved}
              manualIdx={manualInfo?.idx}
              hideTestPaper={hideTestPaper}
              checkedUnitIdxList={checkedUnitIdxList}
              generatedPaper={generatedPaper}
              config={testPaperConfig}
              paperTab={paperTab}
              setDownloadModalOpen={setDownloadModalOpen}
              isLoadMode={location?.state?.testPaper ? true : false}
              loadedPaperIdx={location?.state?.testPaper?.idx}
              printPdf={printPdf}
            />
          </div>
          <div className="grow min-h-[0px] w-full">
            <div className="relative flex h-full justify-start">
              <>
                <UnitChoice
                  manualIdx={manualInfo?.idx}
                  unitList={unitList}
                  checkedUnitIdxList={checkedUnitIdxList}
                  setCheckedUnitIdxList={setCheckedUnitIdxList}
                  setVocabularyList={setVocabularyList}
                  isFolding={isFolding.unit}
                  setIsFolding={setIsFolding}
                  setPrintSetOpen={setPrintSetOpen}
                  savedPaper={location?.state?.testPaper}
                  setGeneratedPaper={setGeneratedPaper}
                  checkedStatistics={checkedStatistics}
                  setCheckedStatistics={setCheckedStatistics}
                  orderingCheck={orderingCheck}
                  isExchange={location?.state?.isExchange}
                />
                <QuestionTypeChoice
                  vocabularyList={vocabularyList}
                  generatedPaper={generatedPaper}
                  setVocabularyList={setVocabularyList}
                  config={testPaperConfig}
                  setConfig={setTestPaperConfig}
                  isFolding={isFolding.type}
                  setIsFolding={setIsFolding}
                  setRecordSetModalOpen={setRecordSetModalOpen}
                  setHideTestPaper={setHideTestPaper}
                  checkedStatistics={checkedStatistics}
                  setCheckedStatistics={setCheckedStatistics}
                />
                <VocabularyChoice
                  vocabularyList={vocabularyList}
                  setVocabularyList={setVocabularyList}
                  isFolding={isFolding.voca}
                  setIsFolding={setIsFolding}
                  isExchange={location?.state?.isExchange}
                  orderingCheck={orderingCheck}
                  setOrderingCheck={setOrderingCheck}
                />
              </>
              <VocaTestPaper
                tab={paperTab}
                setTab={setPaperTab}
                config={testPaperConfig}
                generatedPaper={generatedPaper}
                hideTestPaper={hideTestPaper}
                setHideTestPaper={setHideTestPaper}
                saveFinalVocaList={saveFinalVocaList}
                saveBtnDisabled={
                  vocabularyList.filter(
                    v => v.shouldShow && v.checked && v.chosenType !== null,
                  )?.length === 0
                }
                pdfUrl={pdfUrl}
                setPrintSetOpen={setPrintSetOpen}
                setIsFolding={setIsFolding}
                setPdfUrl={setPdfUrl}
                setPdfBlob={setPdfBlob}
                printSetTrigger={printSetTrigger}
                commonPaperVocaList={commonPaperVocaList}
                isLoadMode={location?.state?.testPaper ? true : false}
                printSetOpen={printSetOpen}
              />
              <div
                className="h-full w-[440px] min-w-[px] transition-all bg-[#969ba4] overflow-hidden"
                style={{
                  width: printSetOpen.set || printSetOpen.print ? '440px' : '0',
                  minWidth:
                    printSetOpen.set || printSetOpen.print ? '440px' : '0',
                }}
              />
              <PrintSet
                open={printSetOpen}
                config={testPaperConfig}
                setConfig={setTestPaperConfig}
                paperTab={paperTab}
                setTab={setPaperTab}
                setPrintSetTrigger={setPrintSetTrigger}
                loadedPaperData={location?.state?.testPaper}
                isExchange={location?.state?.isExchange}
              />
            </div>
          </div>
        </section>
      </div>
      <DownloadModal
        open={dowloadModalOpen}
        setOpen={setDownloadModalOpen}
        config={testPaperConfig}
        generatedPaper={generatedPaper}
        paperTab={paperTab}
        downloadPdf={downloadPdf}
        commonPaperVocaList={commonPaperVocaList}
        audioGapAmount={audioGapAmount}
      />
      <RecordSetModal
        open={recordSetModalOpen}
        setOpen={setRecordSetModalOpen}
        audioType={audioType}
        setAudioType={setAudioType}
        audioGapAmount={audioGapAmount}
        setAudioGapAmount={setAudioGapAmount}
        setHideTestPaper={setHideTestPaper}
      />
      <AlertModal
        open={failedVocaAlert?.length >= 1}
        setOpen={setFailedVocaAlert}
        title={`아래 어휘는 예제가 부족하여\n객관식 유형으로 출제가 불가능합니다.\n〈단어〉 탭에서 어휘 왼쪽 유형 버튼을 클릭하여\n출제 유형을 변경해 주세요.`}
        content={failedVocaAlert?.map(item => item.vc_word.trim())?.join('\n')}
        confirmLabel="확인"
        onConfirm={() => setFailedVocaAlert([])}
      />
    </main>
  );
}

export default Vocabulary;
